import React from 'react';
import { Card, Button, Skeleton, Alert } from 'antd';
import {
  ShoppingOutlined,
  DollarOutlined,
  HeartOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import type { UserOverviewResponse } from '../../../services/userManagementService';
import { LoyaltyStatusCard, NotesCard } from '../../../components/user-detail';
import { formatVnd } from '../../../services/userManagementService';
import { formatVnCalendarDate, formatVnDate } from '../../../utils/vnDateTime';

const ROUNDED = { borderRadius: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' };

interface OverviewTabProps {
  overview: UserOverviewResponse['data'] | undefined;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onViewPointsHistory: () => void;
  noteValue: string;
  onNoteChange: (v: string) => void;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({
  overview,
  loading,
  error,
  onRetry,
  onViewPointsHistory,
  noteValue,
  onNoteChange,
}) => {
  if (loading && !overview) {
    return (
      <div
        className="merchant-detail-general-info-grid"
        style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24 }}
      >
        <Skeleton active paragraph={{ rows: 8 }} />
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        message="Không thể tải dữ liệu người dùng. Vui lòng thử lại."
        showIcon
        action={
          <Button size="small" onClick={onRetry}>
            Thử lại
          </Button>
        }
      />
    );
  }

  if (!overview) return null;

  const { profile, stats, loyalty, vouchers } = overview;
  const isShadowbanned = Boolean(profile.isShadowbanned);
  const isCodRestricted = Boolean(profile.codRestricted);
  const loyaltyBalance = loyalty.currentPoints;
  const loyaltyLifetime = loyalty.lifetimeEarned;
  const loyaltyExpiring = loyalty.expiringSoon;
  const goal = loyalty.nextTierGoal ?? 5000;
  const nextTierText = loyalty.nextTier
    ? `${(goal - loyaltyBalance).toLocaleString()} points needed for ${
        loyalty.nextTier
      } status`
    : 'Top tier reached';

  return (
    <div
      className="merchant-detail-general-info-grid"
      style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24 }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {isShadowbanned && (
          <Alert
            type="warning"
            showIcon
            message="Shadowban quảng cáo đang bật"
            description="Người dùng này không thấy quảng cáo sponsored (CPA). Có thể do hệ thống chống gian lận hoặc admin thiết lập thủ công."
          />
        )}
        {isCodRestricted && (
          <Alert
            type="error"
            showIcon
            message="User bị chặn COD"
            description={
              profile.codRestrictionReason
                ? `Lý do: ${profile.codRestrictionReason}${
                    profile.codRestrictedUntil
                      ? `. Hết hạn: ${new Date(
                          profile.codRestrictedUntil
                        ).toLocaleString('vi-VN')}`
                      : ''
                  }`
                : 'User không thể thanh toán COD. Admin có thể mở khóa từ nút "Mở khóa COD" ở header.'
            }
          />
        )}
        {/* 3 stat cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 24,
          }}
        >
          <Card style={ROUNDED} bodyStyle={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: '#dbeafe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ShoppingOutlined style={{ fontSize: 20, color: '#2563eb' }} />
              </div>
              <div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>
                  Total Orders
                </div>
                <div
                  style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}
                >
                  {stats.totalOrders ?? '—'}
                </div>
              </div>
            </div>
          </Card>
          <Card style={ROUNDED} bodyStyle={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: '#dcfce7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <DollarOutlined style={{ fontSize: 20, color: '#16a34a' }} />
              </div>
              <div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>
                  Total Spent
                </div>
                <div
                  style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}
                >
                  {stats.totalSpent != null ? formatVnd(stats.totalSpent) : '—'}
                </div>
              </div>
            </div>
          </Card>
          <Card style={ROUNDED} bodyStyle={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: '#ffedd5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <HeartOutlined style={{ fontSize: 20, color: '#ea580c' }} />
              </div>
              <div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>Favorite</div>
                <div
                  style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}
                >
                  {stats.favoriteCategory || stats.favoriteMerchant || '—'}
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Profile Information */}
        <Card title="Profile Information" style={ROUNDED}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px 24px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <MailOutlined style={{ color: '#9ca3af' }} />
              <span>{profile.email || '—'}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <PhoneOutlined style={{ color: '#9ca3af' }} />
              <span>{profile.phone || '—'}</span>
            </div>
            <div>
              <span style={{ color: '#6b7280', marginRight: 8 }}>Gender:</span>
              <span>{profile.gender || '—'}</span>
            </div>
            <div>
              <span style={{ color: '#6b7280', marginRight: 8 }}>
                Date of Birth:
              </span>
              <span>
                {profile.dob
                  ? formatVnCalendarDate(profile.dob, 'D MMM YYYY')
                  : '—'}
              </span>
            </div>
            <div
              style={{
                gridColumn: '1 / -1',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
              }}
            >
              <EnvironmentOutlined style={{ color: '#9ca3af', marginTop: 4 }} />
              <span>{profile.address || '—'}</span>
            </div>
          </div>
        </Card>

        {/* Vouchers */}
        <Card
          title="Vouchers"
          extra={
            vouchers.length > 0 ? (
              <Button type="link" style={{ padding: 0 }}>
                View All
              </Button>
            ) : null
          }
          style={ROUNDED}
        >
          {vouchers.length === 0 ? (
            <div style={{ color: '#6b7280', padding: '16px 0' }}>
              No vouchers available
            </div>
          ) : (
            vouchers.slice(0, 3).map((v, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '8px 0',
                  borderBottom:
                    i < vouchers.length - 1 ? '1px solid #f0f0f0' : 'none',
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: '#fee2e2',
                    color: '#b91c1c',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                  }}
                >
                  %
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, color: '#111827' }}>
                    {v.title}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    Code: {v.code}
                    {v.expiresAt && ` • Expires ${formatVnDate(v.expiresAt)}`}
                  </div>
                </div>
                <span
                  style={{
                    padding: '4px 10px',
                    borderRadius: 9999,
                    fontSize: 12,
                    fontWeight: 500,
                    background:
                      v.status === 'available' ? '#dbeafe' : '#f3f4f6',
                    color: v.status === 'available' ? '#1d4ed8' : '#4b5563',
                  }}
                >
                  {v.status === 'available' ? 'Available' : v.status}
                </span>
              </div>
            ))
          )}
        </Card>
      </div>

      {/* Right sidebar */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <LoyaltyStatusCard
          tier={`${loyalty.tier} Tier`}
          currentPoints={loyaltyBalance}
          goal={goal}
          nextTierText={nextTierText}
        />
        <Card title="" style={ROUNDED}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6b7280' }}>Current Balance</span>
              <span style={{ fontWeight: 600 }}>
                {loyaltyBalance.toLocaleString()} pts
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6b7280' }}>Lifetime Earned</span>
              <span style={{ fontWeight: 600 }}>
                {loyaltyLifetime.toLocaleString()} pts
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6b7280' }}>Expiring Soon</span>
              <span style={{ fontWeight: 600, color: '#dc2626' }}>
                {loyaltyExpiring} pts
              </span>
            </div>
            <Button
              type="default"
              onClick={onViewPointsHistory}
              style={{ marginTop: 8, borderRadius: 8 }}
            >
              View Points History
            </Button>
          </div>
        </Card>
        <NotesCard
          value={noteValue}
          onChange={onNoteChange}
          showSaveButton={false}
        />
      </div>
    </div>
  );
};
